import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { StateService, Transition, TransitionService } from '@uirouter/core';
import { ApiSearch } from 'src/app/api/services/api-utils.service';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { Display } from 'src/app/api/services/display';
import { FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { DISPLAY_ACCESS } from 'src/app/auth/types/access-level';
import { ENTITY_TYPES } from 'src/app/auth/types/entity-type';
import { FolderService } from 'src/app/shared/services/folder.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { RvshareAppDetectionService } from '../../services/rvshare-app-detection.service';
import { ScreenSharingSessionService } from '../../services/screen-sharing-session.service';
import { ScreenSharingService } from '../../services/screen-sharing.service';
import { Session } from '../../types/session';
import { ScreenSharingMode } from '../../types/screen-sharing-mode';
import { MemberType } from '../../types/member-type';

@Component({
  selector: 'moderator-join',
  templateUrl: './moderator-join.component.html',
  styleUrl: './moderator-join.component.scss',
  standalone: false,
})
export class ModeratorJoinComponent implements OnInit {
  loadingDisplays = signal(false);

  folders: FolderItem[] = [];
  availableDisplays: Display[];
  allDisplays: Display[];
  tableColumns = ['name', 'existingSession', 'status'];

  // Multi-select state
  selectedDisplays: Set<string> = new Set();

  search: any = {
    filter: 'licensed:=1&&screenSharingEnabled:=true',
    includeSubcompanies: false,
  };
  filterConfig = {
    placeholder: 'Search Displays',
    borderless: false,
  };
  favoriteFilterConfig = {
    placeholder: 'Search Displays',
  };
  currentFolderId: string;
  activeTabIndex = 0;
  folderService = inject(FolderService);
  private permissionService = inject(PermissionService);
  currentlyExistingSession: Session;
  companyModSessionsPerDisplayId: Record<string, Partial<Session>> = {};

  get foldersSupported(): boolean {
    return this.permissionService.areFoldersSupported(ENTITY_TYPES.DISPLAY);
  }

  get canModerateScreenShare(): boolean {
    return this.permissionService.meetsAccessRequirement({
      requireAccess: DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
      allowSharedAccess: true,
    });
  }

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private loadInFlight: Promise<void> | null = null;

  constructor(
    private displayApiService: DisplayApiService,
    protected userStateService: UserStateService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    protected screenSharingService: ScreenSharingService,
    protected screenSharingSessionService: ScreenSharingSessionService,
    protected transition: Transition,
    private stateService: StateService,
    private transitionService: TransitionService,
    private modalService: ModalService,
  ) {
    this.search.filter =
      this.permissionService.screenSharingDisplayListFilter();
    this.applyFolderRouteParams(this.transition.params().folderId);
    this.applyTabRouteParams(this.transition.params().tab);

    const deregisterTransitionSuccess = this.transitionService.onSuccess(
      { to: 'apps.screen-sharing.moderator-join' },
      (trans) => {
        // Use transition.params(), not trans.to().params — the latter is the state's
        // static declaration (tab would be the ParamDeclaration object, never 'favorites').
        const fromState = trans.from()?.name;
        const fromParams = trans.params('from');
        const toParams = trans.params();

        this.applyFolderRouteParams(toParams.folderId);
        this.applyTabRouteParams(toParams.tab);

        if (fromState === 'apps.screen-sharing.moderator-join') {
          const folderChanged =
            (fromParams.folderId ?? null) !== (toParams.folderId ?? null);
          // Tab-only transitions update the URL/history; folder list data is unchanged.
          if (!folderChanged) {
            if (
              toParams.tab === 'favorites' &&
              fromParams.tab !== 'favorites'
            ) {
              // Favorites need displays from all folders; folder-scoped loads alone
              // never populate `allDisplays` with favorited items outside this folder.
              void this.mergeFavoritesFromGlobalList();
            }
            return;
          }
        }
        // Re-entering from another route (e.g. browser back) must reload so
        // `allDisplays` / favorites are repopulated; internal tab-only exits above.
        this.load();
      },
    );
    this.destroyRef.onDestroy(() => deregisterTransitionSuccess());
  }

  ngOnInit(): void {
    void this.load();
  }

  private applyFolderRouteParams(folderId: string | null | undefined): void {
    if (this.foldersSupported && folderId) {
      this.currentFolderId = folderId;
      this.search.folderId = folderId;
    } else {
      this.currentFolderId = undefined;
      delete this.search.folderId;
    }
  }

  private applyTabRouteParams(tab: string | null | undefined): void {
    this.activeTabIndex = tab === 'favorites' ? 1 : 0;
  }

  /** List search without folder scope so favorited displays in other folders are included. */
  private buildSearchWithoutFolder(): ApiSearch {
    const s = { ...this.search };
    delete s.folderId;
    return s;
  }

  /**
   * Merges a company-wide display list into `allDisplays`. Needed when the Favorites tab
   * is shown while `search.folderId` is set — otherwise only that folder's displays exist
   * in `allDisplays` after a fresh load (e.g. browser back).
   */
  private async mergeFavoritesFromGlobalList(): Promise<void> {
    const result = await this.displayApiService.list(
      this.buildSearchWithoutFolder(),
    );
    const items =
      result.items && result.items.length > 0
        ? this.sortDisplays(result.items)
        : [];
    this.mergeIntoAllDisplays(items);
  }

  /** Merges the latest folder list into `allDisplays` by id so Favorites stays in sync. */
  private mergeIntoAllDisplays(incoming: Display[]): void {
    const byId = new Map<string, Display>();
    for (const d of this.allDisplays ?? []) {
      if (d.id) {
        byId.set(d.id, d);
      }
    }
    for (const d of incoming) {
      if (d.id) {
        byId.set(d.id, d);
      }
    }
    const merged = Array.from(byId.values());
    this.allDisplays = merged.length > 0 ? this.sortDisplays(merged) : merged;
  }

  /** Navigates so the tab choice is reflected in the URL (browser history). */
  onTabClicked(index: 0 | 1): void {
    // Use relative '.' so we stay on the same state; `tab` is a dynamic param (see routes)
    // so the view is not destroyed and load() is not run again from tab-only URL updates.
    this.applyTabRouteParams(index === 1 ? 'favorites' : null);
    this.stateService.go('.', {
      folderId: this.currentFolderId || null,
      tab: index === 1 ? 'favorites' : null,
    });
    this.cdr.detectChanges();
  }

  async load(): Promise<void> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }
    this.search.filter =
      this.permissionService.screenSharingDisplayListFilter();
    this.loadingDisplays.set(true);
    this.loadInFlight = (async () => {
      try {
        await Promise.all([
          this.checkForExistingSession(),
          this.getCompanySessionIdByDisplayId(),
        ]);
        await Promise.all([this.loadDisplays(false), this.loadFolders()]);
        if (this.activeTabIndex === 1) {
          await this.mergeFavoritesFromGlobalList();
        }
      } catch (error) {
        console.error('Failed to load displays:', error);
      } finally {
        this.loadingDisplays.set(false);
        this.loadInFlight = null;
      }
    })();
    return this.loadInFlight;
  }

  async checkForExistingSession() {
    try {
      this.currentlyExistingSession =
        await this.screenSharingSessionService.findSessionIfExists();
    } catch (error) {
      console.error('Failed to check for existing session:', error);
    }
  }

  async getCompanySessionIdByDisplayId(): Promise<void> {
    const companyModSessions =
      await this.screenSharingSessionService.getCompanySessions(
        ScreenSharingMode.MODERATED,
      );

    if (companyModSessions) {
      this.companyModSessionsPerDisplayId = {};
      companyModSessions.forEach((session) => {
        const { displayId, displayIds, sessionId, screenSharingMode } = session;

        Object.values(session.members ?? [])
          .filter((member) => member.memberType === MemberType.DISPLAY)
          .forEach((member) => {
            this.companyModSessionsPerDisplayId[member.displayId] = {
              displayId,
              displayIds,
              sessionId,
              screenSharingMode,
            };
          });
      });
    }
  }

  joinExistingSession(session: Partial<Session>) {
    if (session.screenSharingMode !== ScreenSharingMode.MODERATED) return;

    this.screenSharingService.selectedDisplayIds = session.displayIds?.length
      ? session.displayIds
      : [session.displayId];
    this.transition.router.stateService.go(
      'apps.screen-sharing.moderator-room',
      {
        displayId: session.displayId,
        sessionId: session.sessionId,
        rvshare: this.rvshareAppDetectionService.isScreenShareApp(),
        existingSession: true,
      },
    );
  }

  endSession(sessionId: string) {
    this.modalService
      .confirmDanger(
        'End session for all?',
        'All participants will be removed from the room and the sharing session will end.',
        'End Session for All',
        'Cancel',
      )
      .then(async () => {
        this.loadingDisplays.set(true);
        await this.screenSharingSessionService.deleteSession(sessionId);
        this.load();
      })
      .catch((error) => {
        console.error('Failed to end session:', error);
        this.loadingDisplays.set(false);
      });
  }

  loadDisplays = (changeLoading: boolean = true): Promise<void> => {
    if (changeLoading) this.loadingDisplays.set(true);

    return this.displayApiService
      .list(this.search)
      .then((result) => {
        if (result.items && result.items.length > 0) {
          this.availableDisplays = this.sortDisplays(result.items);
        } else {
          this.availableDisplays = [];
        }
        // Keep a merged-by-id list for the Favorites tab. With a persistent component
        // (dynamic route params), we must merge each load; the old `only if !allDisplays`
        // pattern only worked when the view was recreated every navigation.
        this.mergeIntoAllDisplays(this.availableDisplays);
      })
      .finally(() => {
        if (changeLoading) this.loadingDisplays.set(false);
      });
  };

  async loadFolders() {
    if (!this.foldersSupported) {
      this.folders = [];
      return;
    }

    const [folders] = await Promise.all([
      this.folderService.loadFolders('Display', {
        ...this.search,
        folderId: this.currentFolderId || FoldersApiService.ROOT_FOLDER_ID,
      }),
      this.folderService.loadFolderPath(this.currentFolderId),
    ]);
    this.folders = folders;
  }

  get datasource() {
    if (this.search.query || !this.foldersSupported) {
      return this.availableDisplays;
    }
    const displays = this.availableDisplays ?? [];
    return [
      ...this.folders.filter((folder) =>
        this.folderService.showFolderItem(
          this.currentFolderId || FoldersApiService.ROOT_FOLDER_ID,
          folder.parentId,
        ),
      ),
      ...displays.filter((display) =>
        this.folderService.showFolderItem(
          this.currentFolderId || FoldersApiService.ROOT_FOLDER_ID,
          display.folderId,
        ),
      ),
    ];
  }

  get favoritesDataSource() {
    const displays = this.allDisplays ?? [];
    return [
      ...displays.filter(
        (display) =>
          this.isFavored(display) &&
          (!this.search.query ||
            display.name
              .toLowerCase()
              .includes(this.search.query.toLowerCase())),
      ),
    ];
  }

  get displayTableDataSource() {
    return this.activeTabIndex === 0
      ? this.datasource
      : this.favoritesDataSource;
  }

  searchFavorites = () => {};

  getDisplay(displayId: string): Display | undefined {
    return (
      this.availableDisplays?.find((display) => display.id === displayId) ??
      this.allDisplays?.find((display) => display.id === displayId)
    );
  }

  isFavored(display: Display | undefined): boolean {
    const userEmail = this.userStateService.getUserEmail();
    return !!display?.favoredBy?.find((by) => by === userEmail);
  }

  isDisabled(displayId: string): boolean {
    const display = this.getDisplay(displayId);
    if (!display) {
      return true;
    }
    return (
      display.onlineStatus !== 'online' ||
      !!this.companyModSessionsPerDisplayId[display.id!]
    );
  }

  sortDisplays(displayList: Display[]): Display[] {
    const list = displayList ?? [];
    const bucket = (d: Display): number => {
      const favored = this.isFavored(d);
      const online = d.onlineStatus === 'online';
      if (favored && online) {
        return 0;
      }
      if (favored && !online) {
        return 1;
      }
      if (!favored && online) {
        return 2;
      }
      return 3;
    };
    return [...list].sort((a, b) => {
      const diff = bucket(a) - bucket(b);
      if (diff !== 0) {
        return diff;
      }
      return (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        sensitivity: 'base',
      });
    });
  }

  private updateFavorite(display: Display, email: string): void {
    if (this.isFavored(display)) {
      const favoredBy = display.favoredBy;
      if (favoredBy) {
        const index = favoredBy.indexOf(email);
        if (index >= 0) {
          favoredBy.splice(index, 1);
        }
      }
    } else {
      display.favoredBy = display.favoredBy ?? [];
      display.favoredBy.push(email);
    }
  }

  toggleFavorite(displayId: string) {
    const display = this.getDisplay(displayId);
    if (!display) {
      return;
    }
    const email = this.userStateService.getUserEmail();
    this.updateFavorite(display, email);

    display.savingFavorite = true;
    this.displayApiService
      .update(displayId, display)
      .catch((error) => {
        console.error(error);
        this.updateFavorite(display, email);
      })
      .finally(() => {
        display.savingFavorite = false;
        this.availableDisplays = this.sortDisplays(this.availableDisplays);
        this.allDisplays = this.sortDisplays(this.allDisplays);
      });
  }

  onFolderClicked(folderId: string) {
    this.stateService.go('.', {
      folderId: folderId || null,
      tab: this.activeTabIndex === 1 ? 'favorites' : null,
    });
  }

  onRowClicked(row: FolderItem | Display) {
    if ('isFolder' in row && row.isFolder) {
      this.onFolderClicked(row.id);
    } else if (!this.isDisabled(row.id)) {
      this.toggleSelection(row.id);
    }
  }

  isSelected(displayId: string): boolean {
    return this.selectedDisplays.has(displayId);
  }

  toggleSelection(displayId: string, event?: Event): void {
    event?.stopPropagation();
    if (this.selectedDisplays.has(displayId)) {
      this.selectedDisplays.delete(displayId);
    } else {
      this.selectedDisplays.add(displayId);
    }
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedDisplays.clear();
    } else {
      this.getSelectableDisplays().forEach((d) =>
        this.selectedDisplays.add(d.id),
      );
    }
  }

  getSelectableDisplays(): Display[] {
    const source =
      this.activeTabIndex === 1
        ? this.favoritesDataSource
        : this.datasource.filter((d) => !('isFolder' in d && d.isFolder));
    return source.filter(
      (display) =>
        !('isFolder' in display && display.isFolder) &&
        !this.isDisabled(display.id!),
    ) as Display[];
  }

  isAllSelected(): boolean {
    const selectableDisplays = this.getSelectableDisplays();
    return (
      selectableDisplays.length > 0 &&
      selectableDisplays.every((d) => this.selectedDisplays.has(d.id))
    );
  }

  clearSelection(): void {
    this.selectedDisplays.clear();
  }

  startSharingWithSelected(): void {
    const displayIds = Array.from(this.selectedDisplays).filter((id) => {
      const d = this.getDisplay(id);
      return d?.onlineStatus === 'online';
    });

    if (displayIds.length === 0) return;

    this.screenSharingService.selectedDisplayIds = displayIds;

    this.stateService.go('apps.screen-sharing.moderator-room', {
      displayId: displayIds[0],
      sessionId: this.screenSharingSessionService.generateSessionId(),
      existingSession: false,
      rvshare: this.rvshareAppDetectionService.isScreenShareApp(),
    });
  }

  get selectedCount(): number {
    return this.selectedDisplays.size;
  }
}
